/**
 * The fleet-wide availability summary, as context (#2621, R-021, R-041).
 *
 * Two surfaces show the same count: the `App catalog` rail item and the catalogue's fourth
 * attention tile. One fetch feeds both. Two independent fetches of one aggregate can disagree with
 * nothing pinning them, and the pane cannot be handed a prop because `AppLoader` builds every pane
 * element from a manifest `loader` typed `() => Promise<{ default: ComponentType }>` and passes it
 * nothing.
 *
 * Shaped like `grants-context.tsx`: the composition root runs the fetch and holds the state, and
 * this module is a value carrier with no effect of its own (R-041). `AppHost` reads the state as
 * the prop `AuthenticatedShell` carries down (R-018); `AppCatalogPane` reads it through
 * {@link useAvailabilitySummary}.
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { AvailabilitySummaryState } from '../panes/availability-model';

const AvailabilitySummaryCtx = createContext<AvailabilitySummaryState | null>(null);

/**
 * The state a reader who was never going to be allowed to ask should see.
 *
 * Exported and returned by reference from {@link visibleSummaryState} so a consumer memoising on
 * identity does not churn, on `GRANTS_UNKNOWN`'s precedent in `grants-state.ts`.
 */
export const SUMMARY_NOT_FETCHED: AvailabilitySummaryState = { kind: 'not-fetched' };

/** The state a reader whose summary-key pre-check failed should see (R-030). */
export const SUMMARY_FORBIDDEN: AvailabilitySummaryState = { kind: 'forbidden' };

/** The state before the one read settles. The only arm that will change on its own. */
export const SUMMARY_LOADING: AvailabilitySummaryState = { kind: 'loading' };

/**
 * Carries the root's summary state to the whole authenticated subtree.
 *
 * Value-only, like `GrantsStateProvider`: the fetch stays in `CpAdminApp`, which is the only
 * component in this package holding mount-time effects. Mounted beside `GrantsStateProvider`,
 * inside `PermissionProvider` and above `AuthenticatedShell`.
 */
export function AvailabilitySummaryProvider({
  value,
  children,
}: {
  readonly value: AvailabilitySummaryState;
  readonly children: ReactNode;
}): React.JSX.Element {
  return <AvailabilitySummaryCtx value={value}>{children}</AvailabilitySummaryCtx>;
}

/**
 * The one summary state.
 *
 * Throws outside a provider rather than defaulting, on `useGrantsState`'s rule: a default of
 * `{ kind: 'not-fetched' }` would claim a read was never applicable for a subtree nobody told
 * anything, which is the collapse this state's five arms exist to prevent.
 */
export function useAvailabilitySummary(): AvailabilitySummaryState {
  const ctx = useContext(AvailabilitySummaryCtx);
  if (ctx === null) {
    throw new Error('useAvailabilitySummary must be used within an AvailabilitySummaryProvider');
  }
  return ctx;
}

/**
 * Which summary state may be shown, derived during render from the two site-wide pre-checks and
 * whatever has been fetched.
 *
 * `canReadLicences` false is `forbidden`, never `not-fetched` (R-030). A reader who does not hold
 * `AVAILABILITY_SUMMARY_PERMISSION` and a reader the server refuses are one fact about one reader, and
 * `AppCatalogPane.tsx` records `TenantsPane` rendering that fact as "not collected" as a real
 * defect.
 *
 * `canReadApps` false with `canReadLicences` true is `not-fetched`: no surface renders for that
 * principal, because both the rail item and the tile sit behind the catalogue's own
 * `cp:app:read`, so the value reaches no reader and no request is worth spending (R-038).
 *
 * `fetched` null with both checks passing is `loading`: the read is applicable and has not
 * settled.
 *
 * A render-time derivation, and it must stay one. `visibleGrantsState`'s docblock states the
 * reason with its measurement: React flushes passive effects after the commit, so a reset written
 * in an effect always leaves one commit holding the previous value.
 *
 * Every gated path returns a module constant by reference, so a consumer memoising on identity
 * does not churn.
 *
 * On `00-index.md` §4's full-body list, item 7 — "the provider's gate".
 */
export function visibleSummaryState(
  canReadLicences: boolean,
  canReadApps: boolean,
  fetched: AvailabilitySummaryState | null,
): AvailabilitySummaryState {
  if (!canReadLicences) return SUMMARY_FORBIDDEN;
  if (!canReadApps) return SUMMARY_NOT_FETCHED;
  return fetched ?? SUMMARY_LOADING;
}
